import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { SwitchRow } from "components/ui/controls/SwitchRow"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { Input } from "components/ui/inputs/Input"
import styled, { useTheme } from "styled-components"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import { calculationLimits } from "store/calculation.config"
import { useEffect, useMemo, useState } from "react"
import { calculateRetardation } from "./Retardation.calculate"
import { useErrors } from "providers/ErrorsProvider"
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { generateTicks } from "utilities/generateTicks"

import { ReactComponent as StraightRightCrossing } from 'assets/img/retardation_straight_right.svg'
import { ReactComponent as RightRightCrossing } from 'assets/img/retardation_right_right.svg'
import { ReactComponent as LeftRightCrossing } from 'assets/img/retardation_left_right.svg'
import { ReactComponent as LeftStraightCrossing } from 'assets/img/retardation_left_straight.svg'
import { ReactComponent as LeftLeftCrossing } from 'assets/img/retardation_left_left.svg'
import { ContentContainer } from "components/ui/display/ContentContainer";
import { MaterialIcon } from "components/ui/shared/MaterialIcon"

export const Retardation = () => {

    const GlobalTheme = useTheme();
    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        slope: '',
        mainRoadRadius: '',
        rampRadius: '',
        rampCrossSlope: '',
        rampConnectionCrossSlope: '',
        mainRoadCrossSlope: '',
    });

    const [calculationInput, setCalculationInput] = useState({
        strictMode: false,
        designClass: '',
        crossingType: '',
        speedlimit: '',
        slope: '',
        mainRoadRadius: '',
        rampRadius: '',
        rampCrossSlope: '',
        rampConnectionCrossSlope: '',
        mainRoadCrossSlope: '',
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateRetardation(calculationInput);

        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }

        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });

    }, [calculationInput, setError, unsetError]);
    
    const XAxisTicks = useMemo(() => {
        if(!calculatedState.output?.retardationData?.valid) return null;

        const min = Math.min(...calculatedState.output.retardationData.value.map(c => c.distance));
        const max = Math.max(...calculatedState.output.retardationData.value.map(c => c.distance));

        return generateTicks({
            min: Math.round(min),
            max: Math.round(max),
            targetTicks: 10
        });
    }, [calculatedState]);

    const YAxisTicks = useMemo(() => {
        if(!calculatedState.output?.retardationData?.valid) return null;

        const min = Math.min(...calculatedState.output.retardationData.value.map(c => c.speed));
        const max = Math.max(...calculatedState.output.retardationData.value.map(c => c.speed));

        return generateTicks({
            min: min,
            max: max,
            step: 10,
            expand: true
        });
    }, [calculatedState]);

    return (
        <StyledRetardation>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title='Inndata'>
                    <SwitchRow
                        icon={<MaterialIcon name='contract' weight={300}/>}
                        title={"Bruk konsekvent modus"}
                        isChecked={calculationInput.strictMode}
                        onChange={(e) => setCalculationInput(prev => ({...prev, strictMode: e.target.checked}))}
                    />
                    {
                        calculationInput.strictMode ? 
                        <DropdownMenu
                            label="Dimensjoneringsklasse"
                            defaultValue={null}
                            defaultIcon={<MaterialIcon name='road' weight={300}/>}
                            defaultName='Velg dimensjoneringsklasse'
                            defaultSelected={calculationInput.designClass}
                            onSelect={(e) => setCalculationInput(prev => ({...prev, designClass: e}))}
                        >
                            <DropdownOption value='h1-80' name='H1 (80km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h1-90' name='H1 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h2-90' name='H2 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-90' name='H3 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-100' name='H3 (100km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-110' name='H3 (110km/t) - Nasjonal hovedveg'/>
                        </DropdownMenu>
                        : null
                    }
                    <DropdownMenu
                        label="Kurvekombinasjon"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='flyover' weight={300}/>}
                        defaultName='Velg kurvekombinasjon...'
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, crossingType: e}))}}
                    >
                        <DropdownOption value='straight-right' name='Rett - Høyre'/>
                        <DropdownOption value='right-right' name='Høyre - Høyre'/>
                        <DropdownOption value='left-right' name='Venstre - Høyre'/>
                        <DropdownOption value='left-straight' name='Venstre - Rett'/>
                        <DropdownOption value='left-left' name='Venstre - Venstre'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Stigning på hovedvegen"
                        prefix={<span>s</span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.slope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.slope?.severity === 'warning' ?? null}
                        value={rawInput.slope}
                        onChange={(e) => setRawInput(prev => ({...prev, slope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, slope: e.target.value}))}
                    />
                    
                    <Input
                        title="Radius på hovedvegen"
                        helptext="Positiv fortegn indikerer høyrekurve, negativ fortegn indikerer venstrekurve."
                        prefix={<span>R<sub>h,1</sub></span>}
                        sufix='m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.mainRoadRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.mainRoadRadius?.severity === 'warning' ?? null}
                        value={calculationInput.crossingType === 'right-straight' ? '' : rawInput.mainRoadRadius}
                        onChange={(e) => setRawInput(prev => ({...prev, mainRoadRadius: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, mainRoadRadius: e.target.value}))}
                        disabled={calculationInput.crossingType === 'straight-right'}
                    />

                    <Input
                        title="Radius på rampen"
                        prefix={<span>R<sub>h,2</sub></span>}
                        sufix='m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.rampRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampRadius?.severity === 'warning' ?? null}
                        value={calculationInput.crossingType === 'straight-left' ? '' : rawInput.rampRadius}
                        onChange={(e) => setRawInput(prev => ({...prev, rampRadius: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampRadius: e.target.value}))}
                        disabled={calculationInput.crossingType === 'left-straight'}
                    />
                    <Input
                        title="Tverrfall på hovedvegen"
                        prefix={<span>e<sub>1</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.mainRoadCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.mainRoadCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.mainRoadCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, mainRoadCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, mainRoadCrossSlope: e.target.value}))}
                    />
                    <Input
                        title="Tverrfall på rampetilslutning"
                        prefix={<span>e<sub>x</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.rampConnectionCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampConnectionCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.rampConnectionCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, rampConnectionCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampConnectionCrossSlope: e.target.value}))}
                    />
                    <Input
                        title="Tverrfall på rampen"
                        prefix={<span>e<sub>2</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.rampCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.rampCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, rampCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampCrossSlope: e.target.value}))}
                    />
                </ToolsContainer>
                <ToolsContainer title='Faste variabler'>
                    <Input
                        title="Sluttfart"
                        prefix={<span>v<sub>1</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.finalSpeed?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Hjulavstand"
                        prefix={<span>b</span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.wheelbase?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Relativ vertikalfart"
                        prefix={<span>v<sub>vf</sub></span>}
                        sufix='m/m'
                        type='number'
                        value={calculatedState.derived?.relativeVerticalSpeed?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Retardasjon"
                        prefix={<span>r</span>}
                        sufix={<span>m/s<sup>2</sup></span>}
                        type='number'
                        value={calculatedState.derived?.retardation?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Lengde på retardasjonsfelt"
                    prefix={<span>L<sub>1</sub></span>}
                    value={calculatedState.output?.retardationLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overgangsstrekning"
                    prefix={<span>L<sub>2</sub></span>}
                    value={calculatedState.output?.transitionLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overhøydeoppbygging"
                    prefix={<span>L<sub>0,min</sub></span>}
                    value={calculatedState.output?.curvedRunoffLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overhøydeutjevning"
                    prefix={<span>L<sub>x,min</sub></span>}
                    value={calculatedState.output?.tangentialRunoffLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Klotoideparameter"
                    prefix={<span>A<sub>min</sub></span>}
                    value={calculatedState.output?.clothoideParameter?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.input?.crossingType?.valid ? 
                    <ContentContainer padding='lg' className='crossingTypeContainer'>
                        {
                            calculatedState.input.crossingType.value === 'straight-right' ? <StraightRightCrossing/> :
                            calculatedState.input.crossingType.value === 'right-right' ? <RightRightCrossing/> :
                            calculatedState.input.crossingType.value === 'left-right' ? <LeftRightCrossing/> :
                            calculatedState.input.crossingType.value === 'left-straight' ? <LeftStraightCrossing/> :
                            calculatedState.input.crossingType.value === 'left-left' ? <LeftLeftCrossing/> : null
                        }
                    </ContentContainer>
                    : null
                }
                {
                    calculatedState.output?.retardationData?.valid ?
                    <ToolsContainer title="Fart som funksjon av tilbakelagt avstand">
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                //minWidth='0'
                                aspect={undefined}
                            >
                                <LineChart
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                    //data={calculatedState.output.retardationData.value}
                                >
                                    <Legend verticalAlign="bottom" layout="vertical"/>
                                    <XAxis
                                        type="number"
                                        dataKey="distance"
                                        domain={[0, 'dataMax']}
                                        ticks={XAxisTicks}
                                        label={{
                                            value: 'Avstand [m]',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                        fontSize={12}
                                        height={50}
                                    />
                                    <YAxis
                                        dataKey='speed'
                                        domain={[
                                            YAxisTicks[0],
                                            YAxisTicks[YAxisTicks.length-1]
                                        ]}
                                        ticks={YAxisTicks}
                                        label={{
                                            value: 'Hastighet [km/t]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                        fontSize={12}
                                    />
                                    <Line
                                        dataKey={'speed'}
                                        dot={false}
                                        data={calculatedState.output.retardationData.value.filter(d => d.segment === 1)}
                                        stroke={GlobalTheme.colors.errorForeground}
                                        strokeWidth={3}
                                        name='Fart ikke OK.'
                                    />
                                    <Line
                                        dataKey={'speed'}
                                        dot={false}
                                        data={calculatedState.output.retardationData.value.filter(d => d.segment === 2)}
                                        stroke={GlobalTheme.colors.successForeground}
                                        strokeWidth={3}
                                        name='Fart OK.'
                                    />
                                    <Line
                                        dataKey={'speed'}
                                        dot={false}
                                        data={calculatedState.output.retardationData.value.filter(d => d.segment === 3)}
                                        stroke={GlobalTheme.colors.errorForeground}
                                        strokeWidth={3}
                                        legendType="none"
                                    />
                                    <CartesianGrid strokeDasharray='3 3'/>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
        </StyledRetardation>
    )
}

const StyledRetardation = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.graphContainer
    {
        min-height: 300px;
        overflow: auto;
    }

    div.crossingTypeContainer
    {
        //background: ${({theme}) => theme.colors.neutralPrimaryBorder};

        svg
        {
            width: 100%;
            height: auto;
        }
    }
`